import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { Order } from '@/types/order';
import { Modal } from '@/types/modal';
import Table from '@/components/molecules/section/Table';
import Container from '@/components/molecules/section/Container';
import { transformDateString } from '@/utilities/datetime';
import { GridAction } from '@/components/molecules/section/Grid';
import ModalView from '@/components/modals/Modal';
import styles from './Refund.module.scss';
import { DISPLAY_EMPTY_VALUE } from '@/shared/constants';

const COLUMN_MIN_WIDTH = 180;

const View: React.FC<{ order: Order }> = ({ order }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('order.refund.actions.create'),
      onClick: () =>
        setModal({
          kind: 'OrderCreateRefund',
          order,
          onClose: () => setModal(null),
        }),
    },
    {
      kind: 'Button',
      label: t('order.refund.actions.create_extra'),
      onClick: () =>
        setModal({
          kind: 'OrderCreateExtraRefund',
          order,
          onClose: () => setModal(null),
        }),
    },
    {
      kind: 'Button',
      label: t('order.refund.actions.proof_of_refund'),
      disabled: !order.refundList?.length,
      onClick: () =>
        setModal({
          kind: 'ItemProofOfRefund',
          onClose: () => setModal(null),
          refundData: {
            refundList: order.refundList,
            paymentMethod: order?.paymentInfo?.paymentMethod,
            paymentStatus: order?.paymentInfo?.status,
          },
        }),
    },
  ];

  const columns: GridColDef[] = [
    {
      field: 'itemId',
      headerName: t('order.refund.item_id'),
      valueGetter: (params: GridValueGetterParams) => (params.value ? params.value : '-'),
      width: 110,
    },
    {
      field: 'createdAt',
      headerName: t('order.refund.creation_date'),
      valueGetter: (params: GridValueGetterParams) => transformDateString(params.value),
      width: 120,
    },
    { field: 'type', headerName: t('order.refund.type') },
    { field: 'amount', headerName: t('order.refund.amount'), width: 110 },
    { field: 'refundAccount', headerName: t('item.refund.refundAccount'), width: COLUMN_MIN_WIDTH },
    { field: 'comment', headerName: t('order.refund.finance_comment'), width: COLUMN_MIN_WIDTH },
    { field: 'status', headerName: t('order.refund.status') },
    { field: 'internalComment', headerName: t('order.refund.internal_comment'), width: 150 },
    {
      field: 'isApproved',
      headerName: t('order.refund.approved'),
      valueGetter: (params: GridValueGetterParams) => (params.value ? t('common.yes') : t('common.no')),
    },
    {
      field: 'executedAt',
      headerName: t('item.refund.executedAt'),
      valueGetter: (params: GridValueGetterParams) => transformDateString(params.value) || DISPLAY_EMPTY_VALUE,
      width: 130,
    },
  ];

  const getBgColor = (state: number) => {
    switch (state) {
      case 1:
        return styles.green;
      case 2:
        return styles.red;
      case 4:
        return styles.yellow;
      default:
        return '';
    }
  };
  const rows = order.refundList.map((refund, index) => ({
    ...refund,
    refundAccount: refund.refundAccount || DISPLAY_EMPTY_VALUE,
    id: index,
    __className__: getBgColor(refund.state),
  }));

  return (
    <>
      <Container
        title={t('order.refund.title')}
        actions={actions}
        testId="refund_info"
      >
        <Table
          scrollHorizontal
          rows={rows}
          columns={columns}
          loading={false}
          table={{ kind: 'PaginationTable', pageSize: 20, pageSizeOptions: [20, 50, 100] }}
        />
      </Container>
      <ModalView modal={modal} />
    </>
  );
};

export default View;
